/**
 * Standard SPARQL 1.1 backend — for uploaded ontologies and non-Wikidata
 * endpoints. Human labels come from `OPTIONAL { ?x rdfs:label ?xLabel }` rather
 * than Wikidata's label service. Prefixes are taken from the source (an uploaded
 * ontology contributes its own `@prefix` declarations).
 */
import type { Filter, QueryIR, Term, TriplePattern } from "../ir/types.ts";
import { PrefixTable } from "../rdf/prefixes.ts";
import { RDFS_LABEL } from "../rdf/ontology.ts";
import type { Source } from "../sources/types.ts";
import type { CandidateQuery, SparqlBackend } from "./types.ts";

const DEFAULT_SELECT_LIMIT = 200;

export class GenericBackend implements SparqlBackend {
  readonly id = "generic";
  private readonly prefixes: PrefixTable;
  private readonly defaultLimit: number;

  constructor(prefixes?: PrefixTable, defaultLimit = DEFAULT_SELECT_LIMIT) {
    this.prefixes = prefixes ?? new PrefixTable();
    this.defaultLimit = defaultLimit;
  }

  supports(source: Source): boolean {
    return source.backendId === this.id;
  }

  emit(ir: QueryIR, _source?: Source): CandidateQuery[] {
    return [{ sparql: this.serialize(ir), strategy: "primary", description: describe(ir) }];
  }

  private term(t: Term): string {
    if (t.kind === "var") return "?" + t.name;
    if (t.kind === "iri") {
      const c = this.prefixes.compact(t.value);
      return c === t.value ? `<${t.value}>` : c;
    }
    const quoted = JSON.stringify(t.value);
    if (t.language) return `${quoted}@${t.language}`;
    if (t.datatype) {
      const dt = this.prefixes.compact(t.datatype);
      return `${quoted}^^${dt === t.datatype ? `<${t.datatype}>` : dt}`;
    }
    return quoted;
  }

  private patternLine(p: TriplePattern): string {
    const line = `${this.term(p.subject)} ${this.term(p.predicate)} ${this.term(p.object)} .`;
    return p.optional ? `OPTIONAL { ${line} }` : line;
  }

  private filterLine(f: Filter): string {
    return `FILTER(?${f.variable} ${f.op} ${this.term(f.value)})`;
  }

  /** Emit `PREFIX` lines only for prefixes that actually appear in the query. */
  private headerFor(query: string): string {
    const used = new Set<string>();
    for (const m of query.matchAll(/(?:^|[\s(])([A-Za-z][\w-]*):[A-Za-z]/g)) {
      if (m[1]) used.add(m[1]);
    }
    const header = this.prefixes.header([...used]);
    return header ? header + "\n" : "";
  }

  private serialize(ir: QueryIR): string {
    const whereLines = ir.patterns.map((p) => "  " + this.patternLine(p));
    for (const f of ir.filters) whereLines.push("  " + this.filterLine(f));

    let body: string;
    if (ir.form === "ask") {
      body = `ASK {\n${whereLines.join("\n")}\n}`;
    } else if (ir.form === "count") {
      const countVar = ir.projection[0] ?? ir.focusVar;
      const inner = ir.distinct ? `DISTINCT ?${countVar}` : `?${countVar}`;
      body = `SELECT (COUNT(${inner}) AS ?count) WHERE {\n${whereLines.join("\n")}\n}`;
    } else {
      const labelPredicate = this.prefixes.compact(RDFS_LABEL);
      const selectVars: string[] = [];
      const labelLines: string[] = [];
      for (const v of ir.projection) {
        selectVars.push(`?${v}`);
        if (ir.wantLabels && v !== ir.orderBy?.variable) {
          selectVars.push(`?${v}Label`);
          labelLines.push(`  OPTIONAL { ?${v} ${labelPredicate} ?${v}Label . }`);
        }
      }
      const distinct = ir.distinct ? "DISTINCT " : "";
      body = `SELECT ${distinct}${selectVars.join(" ")} WHERE {\n${[...whereLines, ...labelLines].join("\n")}\n}`;
      if (ir.orderBy) body += `\nORDER BY ${ir.orderBy.descending ? "DESC" : "ASC"}(?${ir.orderBy.variable})`;
      body += `\nLIMIT ${ir.limit ?? this.defaultLimit}`;
    }

    return this.headerFor(body) + body;
  }
}

function describe(ir: QueryIR): string {
  switch (ir.form) {
    case "count":
      return "Counts distinct matches with COUNT(DISTINCT …).";
    case "ask":
      return "Yes/no existence check with ASK.";
    default:
      return ir.orderBy
        ? `Ranks by ?${ir.orderBy.variable} and takes the top result; labels via rdfs:label.`
        : "Selects matches; labels via OPTIONAL rdfs:label.";
  }
}
